import { chunk } from "lodash";
import PropTypes from "prop-types";
import React, { memo } from "react";
import { Link } from "react-router-dom";
import { htmlEncode } from "../../util";
import VisualizationTooltip from "./VisualizationTooltip";

import { route } from "../../../../shim/jsbridge";

const getRoute = (hash, sourceId, section, qp) => {
  if (!sourceId) {
    return route("redirect_samples_details_section", [hash, section], qp);
  }
  return route("samples_details_section", [hash, sourceId, section], qp);
};

class Visualization extends React.Component {
  constructor(props) {
    super(props);

    this.state = {
      activeRegionClass: null,
      tooltipData: {
        instructions: null,
        names: null,
        position: {
          top: null,
          left: null
        },
        pages: {
          total: 1,
          currentPage: 1
        },
        tooltipKey: null
      },
      isTooltipOpen: false
    };

    this.svgParams = {
      blockSize: 8,
      pColumns: 64,
      tooltipW: 650
    };

    this.queryObject = this.props.relationshipId
      ? { r: this.props.relationshipId }
      : {};

    this.regionLinks = {
      "resource-table": getRoute(
        this.props.sha1,
        this.props.sourceID,
        "ticore/application/pe/resources",
        this.queryObject
      ),
      "import-table": getRoute(
        this.props.sha1,
        this.props.sourceID,
        "ticore/application/pe/imports",
        this.queryObject
      ),
      overlay: getRoute(
        this.props.sha1,
        this.props.sourceID,
        "ticore/application/pe/overlay",
        this.queryObject
      ),
      "pe-header": getRoute(
        this.props.sha1,
        this.props.sourceID,
        "ticore/application/pe/optional_header",
        this.queryObject
      ),
      "export-table": getRoute(
        this.props.sha1,
        this.props.sourceID,
        "ticore/application/pe/exports",
        this.queryObject
      ),
      sections: getRoute(
        this.props.sha1,
        this.props.sourceID,
        "ticore/application/pe/sections",
        this.queryObject
      )
    };

    this.tooltipRef = React.createRef();
    this.containerRef = React.createRef();
  }

  getFunctionsData = type => {
    const sourceData = this.props.structure.plot.matrix.functions;
    const result = { menu: [], functions: {} };
    for (const functions of sourceData) {
      const { names } = functions;
      if (!result.menu.includes(names[0].type)) {
        result.menu.push(names[0].type);
      }
      result.functions[functions.block] = functions;
    }
    return result[type];
  };

  regionMachineName = element => {
    return htmlEncode(element.name.toLowerCase().replace(/\s/g, "-"));
  };

  calculateSvgHeight = () => {
    const { blocks, extras } = this.props.structure.plot.total;
    const extrasCount = extras ? parseInt(extras, 10) : 0;
    const rowNumbers =
      Math.ceil(
        (parseInt(blocks, 10) + extrasCount) / this.svgParams.pColumns
      ) + 1;
    return rowNumbers * this.svgParams.blockSize;
  };

  generateRangesItems = () => {
    const { structure } = this.props;
    const { ranges } = structure.plot.matrix;
    const columns = parseInt(structure.plot.columns, 10);
    const blockSize = this.svgParams.blockSize;

    const result = ranges.map((range, index) => {
      const machineName = htmlEncode(range.name.toLowerCase().replace(".", ""));
      const topPosition = (range.first / columns) * blockSize - blockSize;
      let regionLink;
      if (range.name === "header") {
        regionLink = this.regionLinks["pe-header"];
      } else if (range.name === "overlay") {
        regionLink = this.regionLinks.overlay;
      } else {
        regionLink = this.regionLinks.sections;
      }
      return (
        <div
          className={machineName}
          style={{ top: topPosition + 2 }}
          key={index}
        >
          <Link to={regionLink}>{range.name}</Link>
        </div>
      );
    });

    return result;
  };

  defineStyles = type => {
    const { styles } = this.props.structure.plot.matrix;
    const stylesData = {};

    styles[type].forEach(element => {
      const machineName =
        type === "region"
          ? this.regionMachineName(element)
          : `entropy-${element.style}`;
      stylesData[element.style] = {
        id: element.style,
        name: element.name,
        machine: machineName
      };
    });

    return stylesData;
  };

  mapActiveElements = type => {
    const { structure } = this.props;
    const topLevelElements = ["blocks", "extras"];
    const activeElements = [];

    topLevelElements.forEach(element => {
      const blocks = structure.plot.matrix[element];
      if (!blocks) {
        return;
      }

      blocks.forEach(block => {
        activeElements[block[type]] = true;
      });
    });

    return activeElements;
  };

  addActiveMapClass = activeRegionClass => {
    this.setState({
      activeRegionClass: `p-${activeRegionClass} highlight-visualization`
    });
  };

  removeActiveMapClass = () => {
    this.setState({
      activeRegionClass: undefined
    });
  };

  generateRegionMenuListItems = () => {
    const { region } = this.props.structure.plot.matrix.styles;
    const activeElements = this.mapActiveElements("region");
    const functionsMenu = this.getFunctionsData("menu");
    const regionListItems = [];

    region.forEach((element, index) => {
      const machineName = this.regionMachineName(element);
      if (activeElements[element.style]) {
        const itemName = this.regionLinks[machineName] ? (
          <Link to={this.regionLinks[machineName]}>
            {htmlEncode(element.name)}
          </Link>
        ) : (
          <span>{htmlEncode(element.name)}</span>
        );
        regionListItems.push(
          <li
            className={machineName}
            key={index}
            onMouseEnter={() => {
              this.addActiveMapClass(machineName);
            }}
            onMouseLeave={() => {
              this.removeActiveMapClass();
            }}
          >
            <div className={machineName}></div>
            {itemName}
          </li>
        );
      }
    });

    for (let i = 0; i < functionsMenu.length; i += 1) {
      const functionsMenuItem = htmlEncode(functionsMenu[i]);
      regionListItems.push(
        <li
          className={functionsMenuItem}
          onMouseEnter={() => {
            this.addActiveMapClass(functionsMenuItem);
          }}
          onMouseLeave={() => {
            this.removeActiveMapClass();
          }}
          key={`function-${i}`}
        >
          <div className={functionsMenuItem}></div>
          <span>{functionsMenuItem.replace("function", "Entry point")}</span>
        </li>
      );
    }

    return regionListItems;
  };

  generateEntropyMenuListItems = () => {
    const { heatmap } = this.props.structure.plot.matrix.styles;
    const entropyListItems = heatmap.map((element, index) => {
      const machineName = `entropy-${element.style}`;
      return (
        <li
          className={machineName}
          onMouseEnter={() => {
            this.addActiveMapClass(machineName);
          }}
          onMouseLeave={() => {
            this.removeActiveMapClass();
          }}
          key={index}
        >
          <div className={machineName}></div>
          <span>{htmlEncode(element.name)}</span>
        </li>
      );
    });

    return entropyListItems;
  };

  generateSvgRectangles = () => {
    const { structure } = this.props;
    const styleRegion = this.defineStyles("region");
    const styleEntropy = this.defineStyles("heatmap");
    const rectangles = [];
    const topLevelElements = ["blocks", "extras"];
    const functions = this.getFunctionsData("functions");
    let rectangleCoordX = 0;
    let rectangleCoordY = 0;
    let rectangleCount = 0;

    topLevelElements.forEach(element => {
      const blocks = structure.plot.matrix[element];
      if (!blocks) {
        return;
      }

      blocks.forEach(block => {
        const entries = parseInt(block.entries, 10);
        for (let i = 0; i < entries; i += 1) {
          const machineClassName =
            this.props.previewType === "structure"
              ? styleRegion[block.region].machine
              : styleEntropy[block.entropy].machine;
          let functionClassName = null;
          let instructions = null;
          let names = null;

          if (rectangleCount in functions) {
            instructions = functions[rectangleCount].instructions;
            names = functions[rectangleCount].names;
            functionClassName = `p-${names[0].type}`;
          }

          rectangles.push(
            <rect
              width="7"
              height="7"
              className={`${machineClassName} ${functionClassName || ""}`}
              x={rectangleCoordX * 8}
              y={rectangleCoordY * 8}
              key={rectangleCount}
              data-key={names && rectangleCount}
              onMouseEnter={event => {
                if (names) {
                  this.populateTooltip(instructions, names, event);
                }
              }}
            >
              <title>{styleRegion[block.region].name}</title>
            </rect>
          );
          rectangleCoordX += 1;
          if (rectangleCoordX === this.svgParams.pColumns) {
            rectangleCoordX = 0;
            rectangleCoordY += 1;
          }
          rectangleCount += 1;
        }
      });
    });

    const result = chunk(rectangles, 100);
    return result;
  };

  changeTooltipPage = direction => {
    const { tooltipData } = this.state;
    const pages = tooltipData.pages;
    let pageNumber = pages.currentPage;
    if (direction === "next" && pages.currentPage !== pages.total) {
      pageNumber += 1;
    } else if (direction === "prev" && pages.currentPage !== 1) {
      pageNumber -= 1;
    }
    tooltipData.pages.currentPage = pageNumber;
    this.setState({ tooltipData });
  };

  openTooltip = () => {
    const { isTooltipOpen } = this.state;
    if (!isTooltipOpen) {
      document.addEventListener("mousedown", this.handleClickOutsideTooltip);
    }
    this.setState({ isTooltipOpen: true });
  };

  closeTooltip = () => {
    const { tooltipData } = this.state;
    tooltipData.tooltipKey = null;
    document.removeEventListener("mousedown", this.handleClickOutsideTooltip);
    this.setState({ tooltipData, isTooltipOpen: false });
  };

  handleClickOutsideTooltip = e => {
    const tooltip = this.tooltipRef.current;
    if (!tooltip.contains(e.target)) {
      this.closeTooltip();
    }
  };

  populateTooltip = (instructions, names, rectangle) => {
    const { tooltipData, isTooltipOpen } = this.state;
    const containerPosition = this.containerRef.current.getBoundingClientRect();
    const previousTooltipKey = tooltipData.tooltipKey;
    const newTooltipKey = rectangle.target.getAttribute("data-key");

    if (newTooltipKey === previousTooltipKey) {
      return;
    }

    tooltipData.instructions = instructions;
    tooltipData.names = names;
    tooltipData.pages.total = tooltipData.names.length;
    tooltipData.pages.currentPage = 1;
    tooltipData.position.top =
      rectangle.pageY - containerPosition.top + 5 - window.scrollY;
    tooltipData.position.left =
      rectangle.pageX - containerPosition.left + 4 - window.scrollX;
    tooltipData.tooltipKey = newTooltipKey;
    if (!isTooltipOpen) {
      this.openTooltip();
    }
    this.setState({ tooltipData });
  };

  checkForErrorMessage = (hasPlot, hasRanges, sha1) => {
    let errorMessage;
    if (!hasPlot) {
      errorMessage = `Failed to generate structure plot for file ${sha1}`;
    } else if (!hasRanges) {
      // no ranges means that pyplotter bug TCB-3079 has occurred
      errorMessage = "File Visualization is not available at this time";
    }
    return errorMessage;
  };

  prepareVisualizationData = () => {
    const { previewType } = this.props;
    return {
      ranges: this.generateRangesItems(),
      svgHeight: this.calculateSvgHeight(),
      svgRectangles: this.generateSvgRectangles(),
      sidebarType: previewType === "structure" ? "region" : "entropy",
      sidebarItems:
        previewType === "structure"
          ? this.generateRegionMenuListItems()
          : this.generateEntropyMenuListItems()
    };
  };

  render() {
    const { activeRegionClass, tooltipData, isTooltipOpen } = this.state;
    const { previewType, hasPlot, structure, sha1 } = this.props;
    const title = hasPlot
      ? structure.plot.title
      : "Can't generate structure plot!";
    const hasRanges = hasPlot && structure.plot.matrix.ranges;
    const errorMessage = this.checkForErrorMessage(hasPlot, hasRanges, sha1);
    const showVisualization = hasPlot && hasRanges;
    const values = showVisualization && this.prepareVisualizationData();

    return (
      <div
        ref={this.containerRef}
        id="file-visualization-module"
        className={`file-visualization file-visualization--${previewType} ${activeRegionClass}`}
      >
        <h2 className="file-visualization__title">{title}</h2>
        {errorMessage && <p>{errorMessage}</p>}

        {showVisualization && (
          <>
            <div
              className="file-visualization__ranges"
              style={{ height: values.svgHeight }}
            >
              {values.ranges.map(range => range)}
            </div>

            <svg
              className="file-visualization__blocks"
              width="512"
              height={values.svgHeight}
            >
              {values.svgRectangles.map(svgRectangle => svgRectangle)}
            </svg>

            <ul
              className={`file-visualization__sidebar-list file-visualization__sidebar-list--${values.sidebarType}`}
            >
              {values.sidebarItems.map(sidebarItem => sidebarItem)}
            </ul>

            {isTooltipOpen && (
              <VisualizationTooltip
                title={title}
                tooltipData={tooltipData}
                closeTooltip={this.closeTooltip}
                changeTooltipPage={this.changeTooltipPage}
                svgParams={this.svgParams}
                ref={this.tooltipRef}
              />
            )}
          </>
        )}
      </div>
    );
  }
}

Visualization.propTypes = {
  previewType: PropTypes.string,
  sha1: PropTypes.string,
  sourceID: PropTypes.number,
  relationshipId: PropTypes.number,
  structure: PropTypes.shape(),
  hasPlot: PropTypes.bool,
  plot: PropTypes.shape()
};

export default memo(Visualization);
